import { Injectable } from '@angular/core';
import { map } from 'rxjs';
import { ApiService } from '../../core/services/api.service';
import { ApiEnvelope } from '../models/content.models';
import type { PaginatedTimeline } from './lead-notes.service';

export type ApplicantDetailArea = 'platform' | 'tenant';

export type ApplicantNoteDto = {
  id: string;
  applicantId: string;
  note: string;
  createdBy: string;
  isArchived?: boolean;
  createdAt?: string;
  updatedAt?: string;
};

@Injectable({ providedIn: 'root' })
export class ApplicantNotesService {
  constructor(private readonly api: ApiService) {}

  getTimeline(
    area: ApplicantDetailArea,
    applicantId: string,
    page = 1,
    limit = 20,
    includeArchivedNotes = false,
  ) {
    const prefix = area === 'tenant' ? 'tenant/applicants' : 'platform/applicants';
    const query = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });
    if (includeArchivedNotes) {
      query.set('includeArchivedNotes', 'true');
    }
    return this.api
      .get<ApiEnvelope<PaginatedTimeline>>(`${prefix}/${applicantId}/timeline?${query.toString()}`)
      .pipe(map((r) => r.data));
  }

  createNote(area: ApplicantDetailArea, applicantId: string, note: string) {
    const prefix = area === 'tenant' ? 'tenant/applicants' : 'platform/applicants';
    return this.api
      .post<ApiEnvelope<ApplicantNoteDto>>(`${prefix}/${applicantId}/notes`, { note })
      .pipe(map((r) => r.data));
  }

  updateNote(area: ApplicantDetailArea, applicantId: string, noteId: string, note: string) {
    const prefix = area === 'tenant' ? 'tenant/applicants' : 'platform/applicants';
    return this.api
      .patch<ApiEnvelope<ApplicantNoteDto>>(`${prefix}/${applicantId}/notes/${noteId}`, { note })
      .pipe(map((r) => r.data));
  }

  deleteNote(area: ApplicantDetailArea, applicantId: string, noteId: string) {
    const prefix = area === 'tenant' ? 'tenant/applicants' : 'platform/applicants';
    return this.api
      .delete<ApiEnvelope<{ deleted: boolean }>>(`${prefix}/${applicantId}/notes/${noteId}`)
      .pipe(map((r) => r.data));
  }

  restoreNote(area: ApplicantDetailArea, applicantId: string, noteId: string) {
    const prefix = area === 'tenant' ? 'tenant/applicants' : 'platform/applicants';
    return this.api
      .post<ApiEnvelope<ApplicantNoteDto>>(`${prefix}/${applicantId}/notes/${noteId}/restore`, {})
      .pipe(map((r) => r.data));
  }
}
